// src/components/widgets/AllowedOriginsManager.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
  Stack,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
    AddCircleOutline as AddIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { widgetService } from '../../api/widgetService';

// Basic hostname validation (adjust regex as needed for stricter rules)
// Allows localhost, domain.tld, sub.domain.tld. Disallows http/https, paths, query params, ports for simplicity.
const HOSTNAME_REGEX = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}|localhost$/;

const AllowedOriginsManager = ({ templateId }) => {
  const [origins, setOrigins] = useState([]); // Stores { id: string, origin: string } objects
  const [newOriginInput, setNewOriginInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null); // For API errors
  const [clientError, setClientError] = useState(null); // For input validation errors

  const fetchOrigins = useCallback(async () => {
    if (!templateId) return;
    setIsLoading(true);
    setError(null);
    const result = await widgetService.getTemplateAllowedOrigins(templateId);
    if (result.isError) {
      setError(result.message || 'Failed to load allowed origins.');
      toast.error(result.message || 'Failed to load allowed origins.');
      setOrigins([]);
    } else {
      setOrigins(result); // Expecting Array<{id: string, origin: string}>
    }
    setIsLoading(false);
  }, [templateId]);

  useEffect(() => {
    fetchOrigins();
  }, [fetchOrigins]);

  const validateOrigin = (origin) => {
    const trimmedOrigin = origin.trim().toLowerCase();
    if (!trimmedOrigin) {
      return "Origin cannot be empty.";
    }
    if (!HOSTNAME_REGEX.test(trimmedOrigin)) {
      return "Invalid format. Enter only the hostname (e.g., 'www.example.com' or 'localhost'). Do not include 'http://', 'https://', port numbers, or paths.";
    }
    if (origins.some(o => o.origin.toLowerCase() === trimmedOrigin)) {
      return "This origin hostname is already in the list.";
    }
    return null; // No error
  };

  const handleAddOrigin = () => {
    const validationError = validateOrigin(newOriginInput);
    if (validationError) {
      setClientError(validationError);
      return;
    }
    setClientError(null);
    const trimmedOrigin = newOriginInput.trim().toLowerCase();
    // Add to local state with a temporary ID (or null) until saved
    setOrigins(prev => [...prev, { id: `temp-${Date.now()}`, origin: trimmedOrigin }]);
    setNewOriginInput('');
  };

  const handleRemoveOrigin = (originToRemove) => {
    // Filter based on the 'origin' string property, case-insensitive
    setOrigins(prev => prev.filter(o => o.origin.toLowerCase() !== originToRemove.toLowerCase()));
  };

  const handleSaveChanges = async () => {
    if (!templateId) return;
    setIsSaving(true);
    setError(null);

    // Extract just the origin strings for the API call
    const originStrings = origins.map(o => o.origin);

    const result = await widgetService.updateTemplateAllowedOrigins(templateId, originStrings);
    setIsSaving(false);

    if (result.isError) {
      setError(result.message || 'Failed to save changes.');
      toast.error(result.message || 'Failed to save changes.');
      // Optional: Re-fetch origins to revert optimistic UI updates on failure?
      // fetchOrigins();
    } else {
      toast.success('Allowed origins updated successfully.');
      setOrigins(result); // Update state with potentially new IDs from backend
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" gutterBottom display="flex" alignItems="center">
        Allowed Origins (Hostnames)
        <Tooltip title="Specify the exact hostnames (e.g., 'www.example.com', 'localhost') where widgets using this template can be embedded. The backend verifies the 'Origin' header against this list.">
          <InfoIcon color="action" sx={{ ml: 1, fontSize: '1rem' }} />
        </Tooltip>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add the hostnames where widgets based on this template are permitted to run. This is a security measure to prevent unauthorized embedding.
      </Typography>

      {isLoading && <Box display="flex" justifyContent="center"><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!isLoading && !error && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField
              label="Add New Hostname"
              variant="outlined"
              size="small"
              value={newOriginInput}
              onChange={(e) => {
                setNewOriginInput(e.target.value);
                setClientError(null); // Clear error on input change
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddOrigin(); }}
              error={!!clientError}
              helperText={clientError || "e.g., mywebsite.com or localhost"}
              sx={{ flexGrow: 1 }}
              disabled={isSaving}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddOrigin}
              disabled={isSaving || !newOriginInput.trim()}
            >
              Add
            </Button>
          </Stack>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, minHeight: '40px', p:1, border: '1px solid', borderColor:'divider', borderRadius: 1 }}>
            {origins.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No origins added yet. Widgets using this template cannot be embedded until origins are added.
              </Typography>
            ) : (
              origins.map((o) => (
                <Chip
                  key={o.id || o.origin} // Use origin as key if id is temporary/null
                  label={o.origin}
                  onDelete={() => handleRemoveOrigin(o.origin)}
                  deleteIcon={<DeleteIcon />}
                  disabled={isSaving}
                  size="small"
                />
              ))
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveChanges}
              disabled={isSaving || isLoading}
            >
              {isSaving ? 'Saving...' : 'Save Origin List'}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default AllowedOriginsManager;